// bluaIQ — client_question card SECTION (flag-gated UI exposure).
// Sealed contract name: `insulation-lane-checkpoint-v1`.
//
// This is the ONLY place the card touches the Shell. It mirrors
// TakeoffExcelExportButton exactly: it asks the server whether THIS account is
// allowlisted for the card (default-deny, fail-closed), and renders NOTHING
// until an explicit `enabled: true`. Flag OFF ⇒ no trace the card exists.
//
// SAFETY IS NOT THE FLAG'S JOB (spec condition 3, user condition 4). The flag
// controls EXPOSURE only. The card's own guards — the exactly-4-key evidence
// allowlist, the raw_text rejection, source_user_truth never verbatim — run
// inside TakeoffClientQuestionCard regardless of the flag. A `true` here grants
// visibility, never safety.
//
// DORMANT IN PRODUCTION TODAY, on purpose: the only payload carrying real
// client_questions is a BENCHMARK set (Spring Garden), which must never reach a
// product surface (benchmark isolation). Live/demo project payloads carry no
// `insulation_lane` block, so even for an allowlisted account this section shows
// the honest empty state until a real, non-benchmark client plan produces
// structured questions. That is correct behaviour, not a limitation.

import { useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'

import { fetchTakeoffClientQuestionAvailability } from './takeoffQuantifyClient'
import { extractRawClientQuestions } from './takeoffClientQuestion'
import { TakeoffClientQuestionList, TakeoffClientQuestionAuditorTable } from './TakeoffClientQuestionCard'

export default function TakeoffClientQuestionSection({
  projectId,
  insulationLane,
  isEs,
  view,
}: {
  /** The route project id. Undefined for demo/synthetic houses (no account
   *  scope) ⇒ the availability check is never made ⇒ the section stays hidden. */
  projectId?: string
  /** `payload.insulation_lane` — the RAW block. Each question is validated
   *  per-card, never up-front, so one bad item cannot break the section
   *  (Codex #467 correction 2). Absent on every live/demo payload today ⇒ empty. */
  insulationLane: unknown
  isEs: boolean
  /** Which surface. 'client' → the friendly cards; 'auditor' → the raw channel
   *  showing source_user_truth / linked_blocking_reason verbatim (Codex #467
   *  correction 3). Both behind the same flag. */
  view: 'client' | 'auditor'
}) {
  const { getToken } = useAuth()

  // UI EXPOSURE FLAG. Starts false and only becomes true on an explicit server
  // `enabled: true`, so the section is absent during the in-flight window too —
  // it never flashes on and then disappears. fetch…Availability is fail-closed.
  const [exposed, setExposed] = useState(false)

  useEffect(() => {
    if (!projectId) {
      setExposed(false)
      return
    }
    let cancelled = false
    void (async () => {
      const allowed = await fetchTakeoffClientQuestionAvailability(projectId, await getToken())
      if (!cancelled) setExposed(allowed)
    })()
    return () => {
      cancelled = true
    }
  }, [projectId, getToken])

  // FLAG OFF ⇒ render NOTHING — not a disabled card, not an empty box. An account
  // outside the allowlist must see no trace the card exists. Placed after the
  // hooks so hook order stays stable across the enabled/disabled transition.
  if (!exposed) return null

  // RAW items — NOT validated up front (Codex #467 correction 2). Each card /
  // auditor row validates its own item, so a single malformed question renders
  // its neutral marker instead of throwing and taking the whole section down.
  // Absent/empty block ⇒ the honest empty state and ZERO rows (spec §6).
  const questions = extractRawClientQuestions(insulationLane)

  return (
    <div className="mt-4">
      {view === 'auditor' ? (
        <TakeoffClientQuestionAuditorTable questions={questions} isEs={isEs} />
      ) : (
        <TakeoffClientQuestionList questions={questions} isEs={isEs} />
      )}
    </div>
  )
}
